import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES, SERVICE_AREA, SLOGAN } from "@/lib/site";

const LINES = ["Luxury", "Marine", "Detailing"];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.14 },
  }),
};

export default function Hero({ onNavigate, ready }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={IMAGES.hero} alt="Luxury boat on water" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/40" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-5 md:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={ready ? { opacity: 1 } : {}}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-6 flex items-center gap-3 text-[11px] md:text-xs uppercase tracking-mega text-gold"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-gold/60" /> {SERVICE_AREA}
        </motion.p>

        <h1 className="font-serif font-light leading-[0.86] text-white tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate={ready ? "show" : "hidden"}
                className={`block ${i === 2 ? "text-gold-gradient italic" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-6 font-serif italic text-2xl md:text-3xl text-gold-gradient"
          data-testid="hero-slogan"
        >
          {SLOGAN}.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-8 max-w-xl text-base md:text-lg font-light text-white/70 leading-relaxed"
          data-testid="hero-subtitle"
        >
          A meticulous boat care collective for owners who refuse to compromise. Yachts, speed
          boats and power boats — hull to helm, we return your vessel to showroom condition.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => onNavigate("#booking")}
            className="inline-flex items-center justify-center bg-gold text-black px-9 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold-light transition-colors duration-300"
            data-testid="hero-book-button"
          >
            Request a Quote
          </button>
          <button
            onClick={() => onNavigate("#services")}
            className="inline-flex items-center justify-center border border-white/30 text-white px-9 py-4 text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors duration-300"
            data-testid="hero-services-button"
          >
            View Services
          </button>
        </motion.div>
      </div>

      <motion.button
        onClick={() => onNavigate("#ethos")}
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : {}}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50 hover:text-gold transition-colors"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}
